import React from 'react';
import PropTypes from 'prop-types';

/**
 * Input/Textarea/Select des Design-Systems (siehe MODERNIZATION_PLAN.md P1.2).
 *
 * - text-base (16px) explizit gesetzt: verhindert iOS-Safari-Auto-Zoom beim Fokus
 *   (unabhängig vom globalen !important-Fallback in public/index.html)
 * - min-h-[48px] Touch-Target (MOBILE_UX_GUIDE.md)
 * - error-Prop: rote Umrandung + aria-invalid; die Fehlermeldung selbst rendert FormField
 *
 * Für Label/Hint/Fehlertext siehe ui/FormField.jsx.
 */

/*
 * `min-w-0` (September 2026): Ein Feld in einer Flex- oder Grid-Zelle darf nie
 * breiter sein als seine Zelle. Ohne diese Angabe gilt `min-width: auto`, und
 * das heisst fuer Datums-/Uhrzeitfelder auf iOS: so breit wie das System will
 * (siehe index.css, "Datums- und Uhrzeitfelder auf dem iPhone").
 *
 * Fokus-Markierung liegt INNEN (September 2026).
 *
 * Vorher: `focus-visible:ring-2` — ein 2px-Ring AUSSERHALB des Feldes. Auf dem
 * Telefon fuehrt schon das Antippen eines Textfeldes zu `:focus-visible` (so
 * ist es spezifiziert: Textfelder gelten immer als tastaturfokussiert), und
 * genau danach schiebt die aufklappende Tastatur die Seite hoch. WebKit zeichnet
 * dabei nur den Kasten des Feldes neu, nicht den Bereich daneben — der Ring
 * blieb als zwei senkrechte lila Striche stehen, die im Screenshot vom 1.9.2026
 * quer ueber "Thema" und "Ort" liefen.
 *
 * `ring-inset` malt dieselbe 2px-Linie innerhalb der Feldkante. Sie bleibt fuer
 * Tastaturbedienung genauso deutlich, kann aber nichts mehr ausserhalb des
 * Feldes hinterlassen. Die Kante wird zusaetzlich eingefaerbt statt
 * durchsichtig geschaltet, damit der Fokus auch bei reduziertem Kontrast sitzt.
 *
 * Ring und eingefärbte Kante liegen aufeinander und lesen sich als EINE
 * Linie. Bis Oktober 2026 kam trotzdem ein zweiter Rand dazu: die globale
 * Fokus-Outline aus index.css, die `focus-visible:outline-none` hier schlug.
 * Seit sie in `:where()` steht, gilt allein diese Markierung.
 *
 * Platzhalter in `text-faint` (Oktober 2026): Er ist ein Hinweis, kein
 * Inhalt, und darf nicht wie ein schon ausgefülltes Feld aussehen.
 */
const FIELD_BASE =
  'w-full min-w-0 min-h-[48px] rounded-xl border bg-surface-raised px-4 py-3 text-base text-foreground ' +
  'placeholder:text-faint transition-colors duration-fast ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary focus-visible:border-primary ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const fieldClasses = (error, className) =>
  [FIELD_BASE, error ? 'border-danger' : 'border-border', className].filter(Boolean).join(' ');

/**
 * Tastatur-Vorgaben je Feldtyp (August 2026).
 *
 * Vorher standen in der App 220 Eingabefelder — und `inputMode`, `enterKeyHint`
 * und `autoComplete` kamen zusammen NULL Mal vor. Auf dem Telefon heisst das:
 * Buchstabentastatur fuer die Altersangabe, keine Passwortmanager-Vorschlaege,
 * und iOS korrigiert eingetippte Codes selbstaendig.
 *
 * Die Werte sind Vorgaben, keine Vorschriften: Sie werden VOR `...rest`
 * ausgebreitet, ein ausdruecklich gesetztes Attribut gewinnt also immer.
 *
 * Bewusst NICHT gesetzt: `autoComplete` fuer Passwortfelder. Ob ein Feld
 * "current-password" (Anmeldung) oder "new-password" (Registrierung, Reset)
 * ist, weiss nur die aufrufende Seite — die falsche Angabe laesst
 * Passwortmanager das alte Passwort ins Neu-Feld schreiben. Das E-Mail-Feld
 * eines Anmeldeformulars bekommt dort `autoComplete="username"`.
 *
 * `autoComplete="off"` NUR, wenn es wirklich gewollt ist (Oktober 2026): Die
 * Android-WebView macht daraus TYPE_TEXT_FLAG_NO_SUGGESTIONS — die Tastatur
 * zeigt dann keine Wortvorschlaege und korrigiert nichts mehr (Chromium,
 * ImeUtils.computeEditorInfo). Fuer Freitext (Titel, Beschreibungen,
 * Skill-Namen) also weglassen. Sinnvoll bleibt es bei Feldern ueber eine
 * ANDERE Person (Admin legt ein Konto an) und bei technischen Kennungen.
 */
const TYPE_DEFAULTS = {
  email: {
    inputMode: 'email',
    autoComplete: 'email',
    autoCapitalize: 'none',
    autoCorrect: 'off',
    spellCheck: false,
  },
  password: { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false },
  number: { inputMode: 'numeric' },
  tel: { inputMode: 'tel', autoComplete: 'tel' },
  url: { inputMode: 'url', autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false },
  search: { inputMode: 'search', enterKeyHint: 'search' },
};

/*
 * `leadingIcon` (September 2026, Paket 12): Suchfelder mit Lupe links gab es in
 * der App an einem Dutzend Stellen — jedes Mal als rohes <input> mit eigenem
 * `relative`-Wrapper, weil ui/Input keinen Icon-Slot kannte (Paket 10 hatte das
 * als Lücke notiert: „ergänzen, nicht umgehen"). Der Wrapper wird nur gerendert,
 * wenn ein Icon übergeben ist; alle bestehenden Aufrufe bleiben ein nacktes
 * <input>. Das Icon ist rein dekorativ (aria-hidden, keine Pointer-Events) —
 * die Beschriftung kommt weiterhin über FormField oder aria-label.
 */
/*
 * `trailingAction` (September 2026, Paket 13): ein Knopf RECHTS im Feld — das Auge
 * zum Passwort-Anzeigen, ein Löschen-Kreuz, ein „Kopieren". Übergeben wird ein
 * fertiges Element (meist <button type="button" aria-label=…>); der Slot setzt es
 * absolut ins Feld und reserviert rechts Platz (pr-12). Vorher bauten die
 * Passwort-Seiten dafür eigene relative-Wrapper mit rohen Knöpfen.
 */
const Input = React.forwardRef(({ error, className = '', leadingIcon: LeadingIcon, trailingAction, ...rest }, ref) => {
  const padding = [LeadingIcon ? 'pl-10' : '', trailingAction ? 'pr-12' : ''].filter(Boolean).join(' ');
  const input = (
    <input
      ref={ref}
      className={fieldClasses(error, padding ? `${padding} ${className}` : className)}
      aria-invalid={error ? 'true' : undefined}
      {...(TYPE_DEFAULTS[rest.type] || {})}
      {...rest}
    />
  );
  if (!LeadingIcon && !trailingAction) return input;
  return (
    <div className="relative">
      {LeadingIcon && (
        <LeadingIcon
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
      )}
      {input}
      {trailingAction && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">{trailingAction}</div>
      )}
    </div>
  );
});
Input.displayName = 'Input';
Input.propTypes = {
  error: PropTypes.bool,
  className: PropTypes.string,
  leadingIcon: PropTypes.elementType,
  /** Knopf rechts im Feld (z. B. Passwort anzeigen); ein fertiges Element mit aria-label */
  trailingAction: PropTypes.node,
};

/** Klassen für einen Knopf im `trailingAction`-Slot: 40px-Ziel, Fokusring, neutral */
export const inputActionClasses =
  'inline-flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

const Textarea = React.forwardRef(({ error, className = '', rows = 3, ...rest }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    className={fieldClasses(error, `min-h-[unset] resize-none ${className}`)}
    aria-invalid={error ? 'true' : undefined}
    {...rest}
  />
));
Textarea.displayName = 'Textarea';
Textarea.propTypes = { error: PropTypes.bool, className: PropTypes.string, rows: PropTypes.number };

const Select = React.forwardRef(({ error, className = '', children, ...rest }, ref) => (
  <select
    ref={ref}
    className={fieldClasses(error, className)}
    aria-invalid={error ? 'true' : undefined}
    {...rest}
  >
    {children}
  </select>
));
Select.displayName = 'Select';
Select.propTypes = { error: PropTypes.bool, className: PropTypes.string, children: PropTypes.node };

export { Input, Textarea, Select };
export default Input;
